/**
 * Google Encoded Polyline Algorithm Format — компактная запись линии строкой.
 * Нужна табличке: параметр `&r=` в QR несёт готовый путь к выходу, и он должен
 * помещаться в код, который человек снимет с расстояния (план 21.09, уровень 0).
 *
 * Снаружи — наш порядок [lon, lat] (GeoJSON, как везде в RVF), внутри формата —
 * lat,lon: перестановка делается здесь, чтобы наружу не протекала чужая
 * конвенция. Битый вход не бросает и не даёт NaN: `decodePolyline` возвращает
 * пустой список (в QR попадает что угодно — царапина на печати, обрезка ссылки).
 */
import { haversineM, type LonLat, projectOnSegment } from './index'

/** Алфавит формата — ASCII 63..126; всё вне диапазона делает строку битой. */
const ASCII_MIN = 63
const ASCII_MAX = 126
/** Больше семи пятибитных групп (сдвиг > 30) одно число занять не может. */
const MAX_SHIFT = 30

/** Зигзаг-кодирование одного числа в цепочку символов. */
function encodeValue(value: number, out: string[]): void {
  let v = value < 0 ? ~(value << 1) : value << 1
  while (v >= 0x20) {
    out.push(String.fromCharCode((0x20 | (v & 0x1f)) + ASCII_MIN))
    v >>>= 5
  }
  out.push(String.fromCharCode(v + ASCII_MIN))
}

/** Линия → строка формата. `precision` 5 — стандарт Google (~1 м). */
export function encodePolyline(points: readonly LonLat[], precision = 5): string {
  const factor = 10 ** precision
  const out: string[] = []
  let prevLat = 0
  let prevLon = 0
  for (const p of points) {
    const lat = Math.round(p[1] * factor)
    const lon = Math.round(p[0] * factor)
    encodeValue(lat - prevLat, out)
    encodeValue(lon - prevLon, out)
    prevLat = lat
    prevLon = lon
  }
  return out.join('')
}

/** Одно число из строки: возвращает значение и новую позицию; null — обрыв. */
function decodeValue(s: string, start: number): { value: number; next: number } | null {
  let result = 0
  let shift = 0
  let i = start
  while (i < s.length) {
    const code = s.charCodeAt(i) - ASCII_MIN
    i++
    if (code < 0 || code > ASCII_MAX - ASCII_MIN) return null
    result |= (code & 0x1f) << shift
    if (code < 0x20) return { value: result & 1 ? ~(result >> 1) : result >> 1, next: i }
    shift += 5
    if (shift > MAX_SHIFT) return null
  }
  return null
}

/** Строка формата → линия [lon, lat]. Битая строка → пустой список. */
export function decodePolyline(s: string, precision = 5): LonLat[] {
  const factor = 10 ** precision
  const points: LonLat[] = []
  let lat = 0
  let lon = 0
  let i = 0
  while (i < s.length) {
    const dLat = decodeValue(s, i)
    if (!dLat) return []
    const dLon = decodeValue(s, dLat.next)
    if (!dLon) return []
    lat += dLat.value
    lon += dLon.value
    points.push([lon / factor, lat / factor])
    i = dLon.next
  }
  return points
}

/**
 * Упрощение Дугласа–Пекера с допуском В МЕТРАХ (а не в градусах: градус
 * долготы на широте Таллина вдвое короче градуса широты, и допуск в градусах
 * резал бы линию неравномерно). Расстояние до отрезка — projectOnSegment.
 * Концы линии сохраняются всегда.
 */
export function simplifyPolyline(points: readonly LonLat[], toleranceM: number): LonLat[] {
  if (points.length <= 2 || toleranceM <= 0) return [...points]
  const keep = new Array<boolean>(points.length).fill(false)
  keep[0] = true
  keep[points.length - 1] = true
  simplifyRange(points, 0, points.length - 1, toleranceM, keep)
  return points.filter((_, i) => keep[i] === true)
}

/** Рекурсивный шаг: самая далёкая от хорды точка либо делит отрезок, либо нет. */
function simplifyRange(
  points: readonly LonLat[],
  from: number,
  to: number,
  toleranceM: number,
  keep: boolean[],
): void {
  if (to - from < 2) return
  const a = points[from]
  const b = points[to]
  if (!a || !b) return
  let worst = -1
  let worstM = toleranceM
  for (let i = from + 1; i < to; i++) {
    const p = points[i]
    if (!p) continue
    // Вырожденная хорда (начало = конец): меряем от точки, иначе всё «рядом».
    const d = haversineM(a, b) === 0 ? haversineM(p, a) : projectOnSegment(p, a, b).distanceM
    if (d > worstM) {
      worstM = d
      worst = i
    }
  }
  if (worst < 0) return
  keep[worst] = true
  simplifyRange(points, from, worst, toleranceM, keep)
  simplifyRange(points, worst, to, toleranceM, keep)
}
